import { Workspace } from '../../imup/lib/toolBoxes/2D'
import { Coordinate } from '../../models/activeDrawable'
import { Region } from '../../models/region'

const getItemSegments = (child: paper.Item) => {
    return (child as paper.Path).segments
}

/**
 * Function defines if opening_location belongs to a region or not
 * @param regions array of Active Floor regions
 * @param locationPath drawable paper.Path
 * @param workspaceTool
 * @returns region_id or null
 */
export const defineLocationRegion = (
    locationPath: paper.Path,
    regions: Region[] | null | undefined,
    workspaceTool: Workspace
): number | null => {
    if (!regions || !regions.length) return null
    let region_id: number | null = null

    for (const region of regions) {
        const regionPath = workspaceTool.getRegionItemByRegionId(region.id)

        // Path with cutouts have segments nested into childrens
        const segments =
            locationPath.segments ??
            locationPath.children
                .map((child) => getItemSegments(child))
                .flat()
                .filter((el) => el !== undefined)

        if (region_id || !segments) break

        // Find the region which contains all points of the path
        for (const segment of segments) {
            if (!regionPath?.contains(segment.point)) {
                break
            }
            region_id = region.id
        }
    }

    return region_id
}

// Helper function to check if a point is inside a polygon using ray casting algorithm
// Reference: https://wrfranklin.org/Research/Short_Notes/pnpoly.html
const isPointInPolygon = (point: [number, number], polygon: [number, number][]): boolean => {
    const [pointX, pointY] = point
    let inside = false

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [currentX, currentY] = polygon[i]
        const [previousX, previousY] = polygon[j]

        // Check if horizontal ray from point crosses this edge
        const currentAbove = currentY > pointY
        const previousAbove = previousY > pointY
        const edgeCrossesRay = currentAbove !== previousAbove

        if (edgeCrossesRay) {
            const slope = (previousX - currentX) / (previousY - currentY)
            const intersectionX = currentX + slope * (pointY - currentY)
            const rayHitsEdge = pointX < intersectionX

            if (rayHitsEdge) {
                inside = !inside
            }
        }
    }

    return inside
}

export function polylineInsidePolygon(polyline: [number, number][], polygon: [number, number][]): boolean {
    if (!polyline || polyline.length === 0) return false
    for (const p of polyline) if (!isPointInPolygon(p, polygon)) return false

    return true
}

// Helper function to check if any geometry coordinate is inside the region
export const hasGeometryInRegion = (
    geometry: { coordinates: Coordinate[] },
    regionCoordinates: [number, number][]
): boolean => {
    if (!geometry.coordinates || geometry.coordinates.length === 0) {
        return false
    }

    // Check if any coordinate of the geometry is inside the region polygon
    return geometry.coordinates.some((coord) => isPointInPolygon(coord, regionCoordinates))
}
